// CE-04g-UI-pivots-parity-diagnostic — read-only diagnostic helpers.
// Used by the Effect 1 pivot mint loop in TradeChart.jsx to surface
// exactly what data Erevis consumes at pivot mint when the flag
// `erevis_pivot_debug` is set in localStorage. Production default is
// silent: zero console output, zero DOM changes.
//
// No rendering, no formula changes, no data-source policy changes.

function isFiniteNumber(x) {
  return typeof x === 'number' && Number.isFinite(x)
}

/**
 * Back-calculate the prior session's H / L / C from a Classic Daily
 * pivot set. Inverts:
 *   P  = (H + L + C) / 3
 *   R1 = 2P - L  →  L = 2P - R1
 *   S1 = 2P - H  →  H = 2P - S1
 *   C  = 3P - H - L
 * Returns { high, low, close, impliedFrom: 'pivots' }, or null if any of
 * pivot, r1, s1 is null or NaN. Math operates on input precision —
 * callers reverse-deriving raw OHLC must pass full-precision pivots.
 *
 * @param {{ pivot:number, r1:number, s1:number, r2?:number, s2?:number, r3?:number, s3?:number } | null} levels
 * @returns {{ high:number, low:number, close:number, impliedFrom:'pivots' } | null}
 */
export function backCalcPriorOhlcFromPivots(levels) {
  if (!levels) return null
  const { pivot, r1, s1 } = levels
  if (!isFiniteNumber(pivot) || !isFiniteNumber(r1) || !isFiniteNumber(s1)) {
    return null
  }
  const low = 2 * pivot - r1
  const high = 2 * pivot - s1
  const close = 3 * pivot - high - low
  return { high, low, close, impliedFrom: 'pivots' }
}

function fmt(n) {
  return isFiniteNumber(n) ? n.toFixed(2) : 'n/a'
}

/**
 * One-line summary of a pivot-debug snapshot. Shape:
 *   [pivot-debug] <symbol> <activeBarSize> | path=<source> | impliedH=<h> impliedL=<l> impliedC=<c> P=<p>
 *
 * Tolerates null impliedPriorOhlc_dailyPath / computedPivots_dailyPath
 * by printing 'n/a' for missing numeric fields — useful when the
 * daily-bar path was not selected.
 *
 * @param {object} snapshot
 * @returns {string}
 */
export function summarizePivotDebug(snapshot) {
  const s = snapshot || {}
  const symbol = s.symbol ?? 'n/a'
  const bar = s.activeBarSize ?? 'n/a'
  const path = s.pivotSourcePath ?? 'n/a'
  const implied = s.impliedPriorOhlc_dailyPath || {}
  const pivots = s.computedPivots_dailyPath || {}
  return `[pivot-debug] ${symbol} ${bar} | path=${path} | impliedH=${fmt(implied.high)} impliedL=${fmt(implied.low)} impliedC=${fmt(implied.close)} P=${fmt(pivots.pivot)}`
}

function isFlagOn() {
  try {
    return (
      typeof localStorage !== 'undefined'
      && localStorage.getItem('erevis_pivot_debug') === '1'
    )
  } catch {
    return false
  }
}

/**
 * Gated console emit. No-op unless `localStorage.erevis_pivot_debug ===
 * '1'`. When active, emits one console.log summary line + one
 * console.table dump of the full snapshot. Production default: silent.
 *
 * @param {object} snapshot
 */
export function emitPivotDebug(snapshot) {
  if (!isFlagOn()) return
  console.log(summarizePivotDebug(snapshot))
  console.table(snapshot)
}
